import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation, useQuery } = ReactQueryV5;
import { httpClient } from "../../../services/httpClient";
import { isImage } from "./utils";
import { isErrorWithStatus } from "api/helpers";
import { apiv2QueryClient } from "../../../apiv2QueryClient";
import { secondsToMilliseconds } from "date-fns";

export type UploadResponse = {
  fileName: string; // The key to send to Miami when getting the attachment
  message: string; // Attachment uploaded
  originalFileName: string;
};

const uploadAttachment = ({
  formData,
  patientUserId,
}: {
  formData: FormData;
  patientUserId: number;
}) =>
  httpClient.post<UploadResponse>(
    `/miami/upload/attachment/${patientUserId}`,
    formData,
  );

export const useUploadAttachment = () => {
  return useMutation({
    mutationKey: ["uploadAttachment"],
    mutationFn: uploadAttachment,
  });
};

type AttachmentResponse = {
  url: string;
  originalFileName: string | null;
};

const getSignedAttachment = async (attachmentKey: string) => {
  const res = await httpClient.get<AttachmentResponse>(
    `/miami/view/attachment/${attachmentKey}`,
  );
  if (!res.data) {
    throw new Error("Attachment not found");
  }

  return res.data;
};

export const useGetSignedAttachment = (attachmentKey: string) => {
  const api = useQuery({
    queryKey: ["getSignedAttachment", attachmentKey],
    queryFn: () => getSignedAttachment(attachmentKey),
    retry(_failureCount, error) {
      return !isErrorWithStatus(error, 404);
    },
    staleTime: secondsToMilliseconds(60),
    refetchInterval: secondsToMilliseconds(60),
  });
  return {
    ...api,
    isImage: isImage(attachmentKey),
  };
};

/**
 * When prefetching signed URLs, if the request fails then React Query will
 * automatically retry the request. This way we can skip the prefetching
 * if the attachment has already been tried. This helps us avoid
 * users hovering over multiple table rows and triggering
 * multiple prefetch requests for the same missing attachment.
 */
const shouldPrefetchSignedUrl = (attachmentKey: string) => {
  const queryCache = apiv2QueryClient.getQueryCache();

  const queryKeys = queryCache
    .getAll()
    .map((cache) =>
      typeof cache.queryKey[1] === "string" ? cache.queryKey[1] : "",
    )
    .filter(Boolean);

  const queryKeySet = new Set(queryKeys);

  return queryKeySet.has(attachmentKey);
};

export const prefetchSignedUrl = async (attachmentKey: string) => {
  if (shouldPrefetchSignedUrl(attachmentKey)) return;

  await apiv2QueryClient.prefetchQuery({
    queryKey: ["getSignedAttachment", attachmentKey],
    queryFn: () => getSignedAttachment(attachmentKey),
    staleTime: secondsToMilliseconds(60),
  });
};

const deleteAttachment = (attachmentKey: string) =>
  httpClient.delete(`/miami/upload/attachment/${attachmentKey}`);

export const useDeleteAttachment = () => {
  return useMutation({
    mutationKey: ["deleteAttachment"],
    mutationFn: deleteAttachment,
  });
};
